// Reusable front+back diagram with one muscle highlighted. Used both by
// the Muscles management page (embedded under the muscle list) and by the
// standalone /manage/diagram verifier.

import { useEffect, useMemo, useRef, useState } from 'react';
import muscleMapJson from '../assets/diagram/muscle-map.json';
import css from './MuscleMapInspector.module.css';

interface MuscleGroup {
  id: string;
  name: string;
}

type View = 'front' | 'back';

const MUSCLE_INDICES = muscleMapJson.muscleIndices as Record<View, Record<string, number[]>>;
const FRONT_URL = '/assets/diagram/front.svg';
const BACK_URL = '/assets/diagram/back.svg';

const MUSCLE_FILL = '#BDBDBD';
const HIGHLIGHT_RED = '#ef4444';
const HIGHLIGHT_RED_SECONDARY = '#7c2222';
const OVERLAY_GREEN = '#4ade80';

export interface ExerciseMuscleHighlight {
  muscle_id: string;
  is_primary: number | boolean;
}

export function MuscleMapInspector({
  activeMuscle,
  exerciseMuscles,
  muscles,
  showAllMapped = false,
  stack = false,
}: {
  /** Single-muscle red highlight. Used by the by-muscle list. */
  activeMuscle?: string | null;
  /** Multi-muscle highlight with primary/secondary distinction. Used by
   *  the by-exercise list. If set, overrides activeMuscle. */
  exerciseMuscles?: ExerciseMuscleHighlight[];
  muscles: MuscleGroup[];
  showAllMapped?: boolean;
  /** Stack front + back vertically instead of side-by-side. */
  stack?: boolean;
}) {
  return (
    <div className={stack ? `${css.canvas} ${css.canvasStack}` : css.canvas}>
      <DiagramView
        view="front"
        url={FRONT_URL}
        activeMuscle={activeMuscle ?? null}
        exerciseMuscles={exerciseMuscles}
        showAllMapped={showAllMapped}
        muscles={muscles}
      />
      <DiagramView
        view="back"
        url={BACK_URL}
        activeMuscle={activeMuscle ?? null}
        exerciseMuscles={exerciseMuscles}
        showAllMapped={showAllMapped}
        muscles={muscles}
      />
    </div>
  );
}

function DiagramView({
  view,
  url,
  activeMuscle,
  exerciseMuscles,
  showAllMapped,
  muscles,
}: {
  view: View;
  url: string;
  activeMuscle: string | null;
  exerciseMuscles?: ExerciseMuscleHighlight[];
  showAllMapped: boolean;
  muscles: MuscleGroup[];
}) {
  const [svgText, setSvgText] = useState<string | null>(null);
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(url)
      .then((r) => r.text())
      .then((t) => {
        if (!cancelled) setSvgText(t);
      })
      .catch(() => {
        if (!cancelled) setSvgText(null);
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  // Stable inner-HTML object so React only re-applies innerHTML when
  // svgText changes (otherwise React would wipe the data-* attributes
  // set during the classification effect on every render).
  const innerHtmlObj = useMemo(
    () => (svgText ? { __html: svgText } : undefined),
    [svgText],
  );

  // Classify paths once + tag each with its full-document index.
  useEffect(() => {
    const host = hostRef.current;
    if (!host || !svgText) return;
    const paths = host.querySelectorAll<SVGPathElement>('path');
    paths.forEach((p, idx) => {
      p.dataset.pathIndex = String(idx);
      const orig = (p.getAttribute('fill') ?? '').toUpperCase();
      p.dataset.kind = orig === MUSCLE_FILL.toUpperCase() ? 'muscle' : 'other';
    });
  }, [svgText]);

  // Apply active highlight on every render where activeMuscle changes.
  useEffect(() => {
    const host = hostRef.current;
    if (!host || !svgText) return;
    const paths = host.querySelectorAll<SVGPathElement>('path[data-kind="muscle"]');

    const allMapped = new Set<number>();
    if (showAllMapped) {
      for (const ids of Object.values(MUSCLE_INDICES[view])) for (const i of ids) allMapped.add(i);
    }

    // Build per-path-index fill from exerciseMuscles (multi-muscle highlight)
    // or activeMuscle (single). Primary muscles beat secondary if both are
    // mapped to the same path.
    const primaryIdx = new Set<number>();
    const secondaryIdx = new Set<number>();
    if (exerciseMuscles && exerciseMuscles.length > 0) {
      for (const em of exerciseMuscles) {
        const idxs = MUSCLE_INDICES[view][em.muscle_id] ?? [];
        const target = em.is_primary ? primaryIdx : secondaryIdx;
        for (const i of idxs) target.add(i);
      }
    } else if (activeMuscle) {
      for (const i of MUSCLE_INDICES[view][activeMuscle] ?? []) primaryIdx.add(i);
    }

    paths.forEach((p) => {
      const idxStr = p.dataset.pathIndex;
      if (idxStr == null) return;
      const idx = Number(idxStr);
      let fill = MUSCLE_FILL;
      let highlighted = false;
      if (showAllMapped && allMapped.has(idx)) fill = OVERLAY_GREEN;
      if (secondaryIdx.has(idx)) {
        fill = HIGHLIGHT_RED_SECONDARY;
        highlighted = true;
      }
      if (primaryIdx.has(idx)) {
        fill = HIGHLIGHT_RED;
        highlighted = true;
      }
      p.setAttribute('fill', fill);
      p.setAttribute('opacity', highlighted ? '0.95' : '1');

      const muscleNames: string[] = [];
      for (const [muscleId, indices] of Object.entries(MUSCLE_INDICES[view])) {
        if (indices.includes(idx)) {
          const m = muscles.find((x) => x.id === muscleId);
          muscleNames.push(m?.name ?? muscleId);
        }
      }
      const prev = p.querySelector('title');
      if (prev) prev.remove();
      const title = document.createElementNS('http://www.w3.org/2000/svg', 'title');
      title.textContent =
        `#${idx}` + (muscleNames.length ? ` · ${muscleNames.join(', ')}` : ' · (unmapped)');
      p.appendChild(title);
    });
  }, [svgText, activeMuscle, exerciseMuscles, showAllMapped, view, muscles]);

  return (
    <div className={css.viewWrap}>
      <h3 className={css.viewTitle}>{view}</h3>
      <div ref={hostRef} className={css.svgHost} dangerouslySetInnerHTML={innerHtmlObj} />
    </div>
  );
}
